import { FC, useState, useEffect } from "react";
import apiService from "@/lib/api-functions/api-service";
import { toast } from "@/hooks/use-toast";

interface Step2Props {
  companyName: string;
  setCompanyName: (value: string) => void;
  invitationCode: string;
  setInvitationCode: (value: string) => void;
}

const Step2: FC<Step2Props> = ({ companyName, setCompanyName, invitationCode, setInvitationCode }) => {
  const [reservedNames, setReservedNames] = useState<string[]>([]);
  const [companyNameError, setCompanyNameError] = useState<string>("");
  const [invitationCodeError, setInvitationCodeError] = useState<string>("");
  const [codeValidationInfo, setCodeValidationInfo] = useState<{
    company_name?: string;
    game_names?: string[];
    role?: string;
  } | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // Load reserved company names on mount
  useEffect(() => {
    const loadReservedNames = async () => {
      const response = await apiService.getReservedCompanyNames();
      if (response.success && response.data) {
        setReservedNames(response.data);
      }
    };
    loadReservedNames();
  }, []);

  // Validate company name against reserved names
  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    setCompanyNameError("");
    
    if (value && reservedNames.includes(value.toLowerCase())) {
      setCompanyNameError("This company name is reserved. Please contact us if you represent this organization.");
    }
  };

  // Validate invitation code
  const handleInvitationCodeChange = async (value: string) => {
    const upperValue = value.toUpperCase();
    setInvitationCode(upperValue);
    setInvitationCodeError("");
    setCodeValidationInfo(null);

    // Only validate if code looks complete (8 characters)
    if (upperValue.length === 8) {
      setIsValidatingCode(true);
      const response = await apiService.validateInvitationCode(upperValue);
      setIsValidatingCode(false);

      if (response.success && response.data?.valid) {
        setCodeValidationInfo({
          company_name: response.data.company_name,
          game_names: response.data.game_names,
          role: response.data.role,
        });
      } else {
        setInvitationCodeError(response.error || "Invalid invitation code");
      }
    } else if (upperValue.length > 8) {
      setInvitationCodeError("Invitation code must be 8 characters");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Company Information
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Create a new company or join an existing one with an invitation code.
        </p>
      </div>

      <div className="space-y-6">
        {/* Company Name Input */}
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
            Company Name
          </label>
          <input
            id="companyName"
            type="text"
            value={companyName}
            onChange={(e) => handleCompanyNameChange(e.target.value)}
            placeholder="Enter your company name"
            disabled={invitationCode.length > 0}
            className={`w-full h-10 px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
              companyNameError ? "border-red-500" : "border-gray-300"
            }`}
          />
          {companyNameError && (
            <p className="text-red-500 text-xs mt-1">{companyNameError}</p>
          )}
        </div>

        {/* OR Separator */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">OR</span>
          </div>
        </div>

        {/* Invitation Code Input */}
        <div>
          <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-2">
            Invitation Code
          </label>
          <input
            id="invitationCode"
            type="text"
            value={invitationCode}
            onChange={(e) => handleInvitationCodeChange(e.target.value)}
            placeholder="Enter 8-character code"
            maxLength={8}
            disabled={companyName.length > 0}
            className={`w-full h-10 px-4 py-2 border rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed uppercase font-mono ${
              invitationCodeError ? "border-red-500" : codeValidationInfo ? "border-green-500" : "border-gray-300"
            }`}
          />
          {isValidatingCode && (
            <p className="text-gray-500 text-xs mt-1">Validating code...</p>
          )}
          {invitationCodeError && (
            <p className="text-red-500 text-xs mt-1">{invitationCodeError}</p>
          )}
          {codeValidationInfo && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-sm">
              <p className="text-sm text-green-800">
                <strong>Valid Code!</strong> You will join <strong>{codeValidationInfo.company_name}</strong> as a <strong>{codeValidationInfo.role}</strong>
                {codeValidationInfo.game_names && codeValidationInfo.game_names.length > 0 && (
                  <> with access to: <strong>{codeValidationInfo.game_names.join(", ")}</strong></>
                )}
              </p>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Have an invitation code from your team? Enter it here to join their company.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step2;

